import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Form, Field } from "react-final-form";
import { connect } from "react-redux";
import formatString from "format-string-by-pattern";
import { debounce as lodashDebounce } from "lodash";

import Logo from "./Logo";
import "./AuthFlow.css";
import AddressHelpModal from "./AddressHelpModal";
import IMG_LOCATION_BG from "../../assets/images/location_bg.png";

import ICON_PERSON from "../../assets/icons/icon-person.svg";
import ICON_ENVELOPE from "../../assets/icons/icon-envelope.svg";
import ICON_KEY from "../../assets/icons/icon-key.svg";
import ICON_FLAG from "../../assets/icons/icon-flag.svg";
import ICON_BUILDING from "../../assets/icons/icon-building.svg";

import { actions as signupActions } from "coupon-common/src/modules/signup";
import { actions as referralCodesActions } from "coupon-common/src/modules/referralCodes";
import { actions as locationActions, duck as locationsDuck } from "coupon-common/src/modules/locations";
import {
  composeSyncValidators,
  required,
  validEmail,
  validPhone,
  dispatchAsAsync,
  getNormalizedEntity,
} from "coupon-common/src/utils";
import { DEFAULT_PHONE_FIELD_MASK } from "coupon-common";

/* ── small inline icons not covered by the provided asset set ── */
function BriefcaseIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 20 20" fill="none">
      <rect x="2" y="6.5" width="16" height="10.5" rx="2" stroke="#3C3C3C" strokeWidth="1.3" />
      <path d="M7 6.5V4.8A1.8 1.8 0 018.8 3h2.4A1.8 1.8 0 0113 4.8v1.7" stroke="#3C3C3C" strokeWidth="1.3" />
      <path d="M2 11h16" stroke="#3C3C3C" strokeWidth="1.3" />
    </svg>
  );
}
function PinIcon() {
  return (
    <svg width="15" height="19" viewBox="0 0 15 19" fill="none">
      <path d="M7.5 18.3S14 11.9 14 7.4a6.5 6.5 0 10-13 0c0 4.5 6.5 10.9 6.5 10.9z" stroke="#3C3C3C" strokeWidth="1.3" />
      <circle cx="7.5" cy="7.4" r="2.4" stroke="#3C3C3C" strokeWidth="1.3" />
    </svg>
  );
}
function GlobeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="8" stroke="#3C3C3C" strokeWidth="1.3" />
      <path d="M1.4 9h15.2M9 1c2.2 2.2 3.4 5 3.4 8s-1.2 5.8-3.4 8c-2.2-2.2-3.4-5-3.4-8S6.8 1.2 9 1z" stroke="#3C3C3C" strokeWidth="1.1" />
    </svg>
  );
}
function ScanIcon() {
  return (
    <svg width="18" height="16" viewBox="0 0 18 16" fill="none">
      <rect x="1" y="1" width="16" height="10.5" rx="1.6" stroke="#3C3C3C" strokeWidth="1.3" />
      <path d="M4.5 15h9M6.5 11.5V15M11.5 11.5V15" stroke="#3C3C3C" strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="9" cy="6" r="2" stroke="#3C3C3C" strokeWidth="1.1" />
    </svg>
  );
}
function ChevronDownIcon() {
  return (
    <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
      <path d="M1 1l4 4 4-4" stroke="#3C3C3C" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <circle cx="6.3" cy="6.3" r="5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 10l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
/* ── inline eye icons, no external asset dependency ── */
function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M1 9s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z" stroke="#8A8A8A" strokeWidth="1.4" strokeLinejoin="round" />
      <circle cx="9" cy="9" r="2.4" stroke="#8A8A8A" strokeWidth="1.4" />
    </svg>
  );
}
function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M1 9s3-6 8-6c1.5 0 2.8.4 3.9 1M17 9s-1.1 2.2-3.1 3.8M9 15c-5 0-8-6-8-6a14.5 14.5 0 013-3.8"
        stroke="#8A8A8A"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M7 9a2 2 0 002.8 1.8M11 9a2 2 0 00-2.7-1.9" stroke="#8A8A8A" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M1 1l16 16" stroke="#8A8A8A" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

/* ── Serviceable Area picker ──
   Mirrors the real data model used by AdditionalLocationsFieldGroup
   (containers/LocationFieldGroup): city search is scoped to one
   selected province at a time — there is no free-text "search every
   city" endpoint. Selected cities across ALL provinces accumulate in
   the single `additional_cities` field (each city object carries its
   own `province` id), while `additionalProvinces` just tracks which
   province's city list is currently being browsed/searched, and
   `searchCities` holds that search text. Same three field names as
   the real container, so the payload shape matches what signup
   expects. UI is our own pill styling, not the shared FormPillSelector. */
function ServiceableAreaSection({ provinceOptions, cityOptions, values, form }) {
  const [query, setQuery] = useState("");
  const additionalProvince = values.additionalProvinces;
  const selected = Array.isArray(values.additional_cities) ? values.additional_cities : [];

  // Provinces that currently have at least one selected city — shown as
  // removable summary pills, same bulk-remove-by-province behavior as
  // AdditionalLocationsFieldGroup's handleProvinceClick.
  const selectedProvinceIds = new Set(selected.map(c => c.province));
  const selectedProvinces = provinceOptions.filter(p => selectedProvinceIds.has(p.id));

  const filteredCityOptions = useMemo(() => {
    if (!additionalProvince?.id) return [];
    const provinceCities = cityOptions.filter(c => c.province === additionalProvince.id);
    if (!query.trim()) return provinceCities;
    const q = query.trim().toLowerCase();
    return provinceCities.filter(c => c.name?.toLowerCase().includes(q));
  }, [additionalProvince, cityOptions, query]);

  const selectedIdsInView = new Set(
    selected.filter(c => c.province === additionalProvince?.id).map(c => c.id),
  );
  const allSelectedInView =
    filteredCityOptions.length > 0 && filteredCityOptions.every(c => selectedIdsInView.has(c.id));

  const toggleCity = city => {
    const isSelected = selected.some(c => c.id === city.id);
    form.change(
      "additional_cities",
      isSelected ? selected.filter(c => c.id !== city.id) : [...selected, city],
    );
  };

  const toggleAllInView = () => {
    const otherProvinceCities = selected.filter(c => c.province !== additionalProvince?.id);
    form.change(
      "additional_cities",
      allSelectedInView ? otherProvinceCities : [...otherProvinceCities, ...filteredCityOptions],
    );
  };

  // Bulk-remove every selected city for a province, then fall back to
  // whichever province the next-remaining selected city belongs to
  // (matches AdditionalLocationsFieldGroup exactly).
  const removeProvince = prov => {
    const remaining = selected.filter(c => c.province !== prov.id);
    const fallbackProvince = provinceOptions.find(p => p.id === remaining[0]?.province);
    form.change("additionalProvinces", fallbackProvince || undefined);
    form.change("additional_cities", remaining);
  };

  return (
    <div className="auth-form-section">
      <div className="auth-section-heading">Cities</div>

      <div className="auth-city-pill-row">
        {provinceOptions.map(p => (
          <button
            key={p.id}
            type="button"
            className={`auth-city-pill${additionalProvince?.id === p.id ? " is-selected" : ""}`}
            onClick={() => form.change("additionalProvinces", p)}
          >
            {p.name}
          </button>
        ))}
      </div>

      {selectedProvinces.length > 0 && (
        <div className="auth-selected-provinces">
          <div className="auth-field-label">Selected Provinces</div>
          <div className="auth-city-pill-row">
            {selectedProvinces.map(p => (
              <button
                key={p.id}
                type="button"
                className="auth-city-pill is-selected"
                onClick={() => removeProvince(p)}
              >
                {p.name} ×
              </button>
            ))}
          </div>
        </div>
      )}

      {additionalProvince?.id && (
        <div className="auth-serviceable-box">
          <label className="auth-field auth-field-plain auth-serviceable-search">
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search for additional cities you deliver to…"
            />
            <SearchIcon />
          </label>

          <div className="auth-city-pill-row">
            <button
              type="button"
              className={`auth-city-pill${allSelectedInView ? " is-selected" : ""}`}
              onClick={toggleAllInView}
              disabled={filteredCityOptions.length === 0}
            >
              All
            </button>
            {filteredCityOptions.map(c => (
              <button
                key={c.id}
                type="button"
                className={`auth-city-pill${selectedIdsInView.has(c.id) ? " is-selected" : ""}`}
                onClick={() => toggleCity(c)}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* Generic text field bound to react-final-form, icon passed as an <img> src path. */
function TextInput({ icon, label, name, type = "text", placeholder, validate, parse, format, maxLength, fullWidth, disabled }) {
  return (
    <Field name={name} validate={validate} parse={parse} format={format}>
      {({ input, meta }) => (
        <label className={`auth-field auth-field-plain${fullWidth ? " auth-field-full" : ""}`}>
          <img src={icon} alt="" className="auth-field-icon" />
          <div>
            <div className="auth-field-label">{label}</div>
            <input {...input} type={type} placeholder={placeholder} maxLength={maxLength} disabled={disabled} />
          </div>
          {meta.touched && meta.error && (
            <span className="auth-field-error">{meta.error}</span>
          )}
        </label>
      )}
    </Field>
  );
}

/* Same as TextInput, but for fields whose icon is an inline SVG component
   (BriefcaseIcon, ScanIcon, GlobeIcon, PinIcon, …) instead of an <img> path. */
function TextInputSvgIcon({ icon: IconComponent, label, name, type = "text", placeholder, validate, parse, disabled }) {
  return (
    <Field name={name} validate={validate} parse={parse}>
      {({ input, meta }) => (
        <label className="auth-field auth-field-plain">
          <IconComponent />
          <div>
            <div className="auth-field-label">{label}</div>
            <input {...input} type={type} placeholder={placeholder} disabled={disabled} />
          </div>
          {meta.touched && meta.error && (
            <span className="auth-field-error">{meta.error}</span>
          )}
        </label>
      )}
    </Field>
  );
}

const phoneFieldMask = formatString(DEFAULT_PHONE_FIELD_MASK);

const validate = v => {
  const e = {};

  if (!v.contact_name) e.contact_name = "First name is required";
  if (!v.lastName) e.lastName = "Last name is required";
  if (!v.email) e.email = "Email is required";
  if (!v.password) {
    e.password = "Password is required";
  } else if (
    v.password.length < 8 ||
    !/\d/.test(v.password) ||
    !/[^A-Za-z0-9]/.test(v.password)
  ) {
    e.password = "Password must be at least 8 characters and include a number and a special character";
  }
  if (!v.confirmPassword) e.confirmPassword = "Confirm password";
  else if (v.password !== v.confirmPassword) e.confirmPassword = "Passwords do not match";
  if (!v.name) e.name = "Business name is required";
  if (!v.street) e.street = "Business address is required";
  if (!v.city?.id) e.city = "Please pick an address with a recognized Canadian city";
  if (v.is_service_area_business) {
    const cities = Array.isArray(v.additional_cities) ? v.additional_cities : [];
    if (cities.length === 0) {
      e.additional_cities = "Please select at least one city.";
    }
  }
  if (!v.agreed_to_terms) {
    e.agreed_to_terms = "You must accept the Terms and Conditions to continue";
  }

  return e;
};

// Unit/suite designators that Google's place details drop. Pulls one out of
// text the user typed or the suggestion description. A designator must be
// followed by a number or a single letter (optionally with digits), so
// street names like "Rue Ste-Catherine" and provinces like "FL" don't match.
const UNIT_PATTERN =
  /\b(?:suite|ste|unit|apt|apartment|room|rm|floor|bldg|building)\b\.?(?:\s+|\s*#\s*)(?:\d[\w-]*|[A-Za-z]\d*\b)|#\s*\w[\w-]*/i;
// "201-250 Front St W" style, where the part before the dash is the unit.
const LEADING_UNIT_PATTERN = /^\s*(\d+[A-Za-z]?)\s*[-\u2013]\s*\d+\b/;

const extractUnit = texts => {
  for (const text of texts) {
    if (!text) continue;
    const match = text.match(UNIT_PATTERN);
    if (match) return match[0].trim();
    const lead = text.match(LEADING_UNIT_PATTERN);
    if (lead) return `#${lead[1]}`;
  }
  return "";
};

export const BusinessDetailsPage = ({
  prefill,
  initialValues,
  onNext,
  onBack,
  code,
  setSignUpFlowActive,
  dispatch,
  locations,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
    const [addressHelp, setAddressHelp] = useState(null); // null, or prefill for AddressHelpModal

  // Same actions/selectors AdditionalLocationsFieldGroup and
  // BusinessInfoEditModal use — kept here rather than a shared hook so
  // this file's data flow stays traceable in one place.
  useEffect(() => {
    dispatch(locationActions.getProvinces());
    dispatch(locationActions.getCities());
  }, [dispatch]);

  const provincesKey = locationsDuck.provincesEntity.key;
  const { entityList: provinceOptions = [] } = getNormalizedEntity(locations, provincesKey);
  const cityOptions = Object.entries(locations?.entities?.cities || {}).map(([id, city]) => ({
    ...city,
    id: city?.id ?? id,
  }));

  // Stable reference: only rebuild when prefill/initialValues actually
  // change, not on every render (e.g. toggling the password eye icon).
  // A fresh object/array here on every render tricks react-final-form
  // into thinking initialValues changed and wipes whatever the user typed.
  const formInitialValues = useMemo(
    () => ({
      name: prefill?.name || "",
      street: prefill?.address || "",
      country: "Canada",
      is_service_area_business: false,
      additional_cities: [],
      agreed_to_terms: false,
      ...initialValues,
    }),
    [prefill, initialValues],
  );

  // Address autocomplete — street stays the only field the user types
  // into directly. City is matched against the real cityOptions we
  // already fetch for the additional-cities picker (the backend's
  // `city` field is a pk FK, not a name) and stored as {id, name};
  // province/postalCode/country are populated as plain strings and
  // rendered disabled below — none of them are hand-editable anymore.
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [resolvingCity, setResolvingCity] = useState(false);
  const [cityError, setCityError] = useState("");
  // Id of the most recently picked address, so a slow response for an
  // earlier pick can't overwrite the form after the user has moved on.
  const latestPlaceIdRef = useRef(null);

  const fetchAddressSuggestions = async input => {
    if (!input) {
      setAddressSuggestions([]);
      return;
    }
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/users/address-autocomplete/?query=${encodeURIComponent(input)}`,
      );
      const data = await res.json();
      setAddressSuggestions(data.predictions || []);
    } catch (err) {
      console.error("[BusinessDetailsPage] Error fetching address suggestions:", err);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchAddressSuggestions = useMemo(() => lodashDebounce(fetchAddressSuggestions, 300), []);

  const fetchPlaceDetails = async (form, placeId, sourceTexts = []) => {
    latestPlaceIdRef.current = placeId;
    setResolvingCity(true);
    setCityError("");
    // Clear any city from a previous pick so it can't be submitted while the
    // new one is resolving.
    form.change("city", null);

    try {
      const baseUrl = process.env.REACT_APP_BASE_URL;

      // Address details and city resolution are independent, so run them in
      // parallel to keep the wait short.
      const [detailsResponse, cityResponse] = await Promise.all([
        fetch(`${baseUrl}/users/place-details/?place_id=${placeId}`),
        fetch(`${baseUrl}/locations/cities/from-place/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ place_id: placeId }),
        }),
      ]);
      const data = await detailsResponse.json();
      const cityData = cityResponse.ok ? await cityResponse.json() : null;

      // User picked a different address while we were waiting -- drop this one.
      if (latestPlaceIdRef.current !== placeId) return;

      if (data) {
              const baseStreet = [data.street_number, data.street].filter(Boolean).join(" ");
        const baseLower = baseStreet.toLowerCase();

        // Street part (before the first comma) of what the user typed / the
        // suggestion. If it contains Google's street, it also carries any
        // suite/unit/etc. the user wrote, so keep the longest such match.
        const typedMatch = sourceTexts
          .map(t => (t || "").split(",")[0].trim())
          .filter(seg => baseLower && seg.toLowerCase().includes(baseLower))
          .sort((a, b) => b.length - a.length)[0];

        let streetLine = typedMatch || baseStreet;

        // "201-250 Front St W" -> "250 Front St W #201" (unit goes at the end).
        const leadingUnit = streetLine.match(/^\s*(\d+[A-Za-z]?)\s*[-\u2013]\s*(?=\d)/);
        if (leadingUnit) {
          streetLine = `${streetLine.slice(leadingUnit[0].length)} #${leadingUnit[1]}`;
        }

        // Google knows a unit the user didn't type.
        if (streetLine && data.subpremise && !streetLine.toLowerCase().includes(data.subpremise.toLowerCase())) {
          streetLine = `${streetLine} #${data.subpremise}`;
        }

        // No typed match (e.g. "Street" vs "St" wording) and no subpremise:
        // fall back to pulling a unit/suite out of the text, wherever it was.
        if (!typedMatch && !data.subpremise) {
          const unit = extractUnit(sourceTexts);
          if (unit && streetLine && !streetLine.toLowerCase().includes(unit.toLowerCase())) {
            streetLine = `${streetLine} ${unit}`;
          }
        }

        if (streetLine) form.change("street", streetLine);

        if (data.province) form.change("province", data.province);
        if (data.postal_code) form.change("postalCode", data.postal_code);
        form.change("country", data.country || "Canada");
      }

      // The backend matches an existing city or creates it, so this is always
      // a real {id, name} the signup serializer will accept.
      if (cityData) {
        form.change("city", { id: cityData.id, name: cityData.name });
      } else {
        setCityError(
          "We couldn't recognize a Canadian city for that address. Please pick a different address.",
        );
      }
    } catch (err) {
      console.error("[BusinessDetailsPage] Error fetching place details:", err);
      if (latestPlaceIdRef.current === placeId) {
        setCityError("Something went wrong looking up that address. Please try again.");
      }
    } finally {
      if (latestPlaceIdRef.current === placeId) {
        setResolvingCity(false);
        setAddressSuggestions([]);
      }
    }
  };

  const onSubmit = async values => {
    setLoading(true);
    setErrorMessage("");

    try {
      setSignUpFlowActive?.(true);

      let referral_code = code;
      if (!code) {
        referral_code = await dispatchAsAsync(referralCodesActions.generateCode(), dispatch);
      }

      // additionalProvinces is UI-only scratch state (which province's
      // city list is currently in view in ServiceableAreaSection) — it
      // isn't a real signup field and shouldn't ride along in the payload.
      const { additionalProvinces, ...restValues } = values;

      // city and additional_cities are passed through as full {id, name}
      // objects (not raw ids) — formatToAccountFormData's formatToPkArgs
      // does its own `.id` extraction for both, per-item for
      // additional_cities. Flattening to ids here double-extracts and
      // sends `.id` on a number, which is always undefined.
      const payload = {
        ...restValues,
        referral_code,
        additional_cities: values.is_service_area_business
          ? Array.isArray(values.additional_cities)
            ? values.additional_cities
            : []
          : [],
      };

      const submitErrors = await dispatchAsAsync(signupActions.signUp(payload), dispatch);

      if (submitErrors) {
        setErrorMessage(submitErrors.message || "Something went wrong. Please try again.");
      } else {
        dispatch(referralCodesActions.resetCodeGenerator());
        onNext(values);
      }
    } catch (err) {
      console.error("Error submitting BusinessDetails:", err);
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
    
  };

  return (
    <div className="auth-page auth-simple-page">
      <img src={IMG_LOCATION_BG} alt="" className="auth-simple-bg" />
      <header className="auth-navbar auth-navbar-simple">
        <Logo />
      </header>

      <div className="auth-card-wrap auth-card-wrap-loose">
        <div className="auth-card auth-card-wide">
          <h2 className="auth-form-title">Business Details</h2>

          <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={formInitialValues}
            keepDirtyOnReinitialize
            render={({ handleSubmit, values, form, submitting }) => (
              <form onSubmit={handleSubmit}>
                {/* ── Personal Information ── */}
                <div className="auth-form-section">
                  <div className="auth-section-heading">Personal Information</div>

                  <div className="auth-form-grid">
                    <TextInput icon={ICON_PERSON} label="First Name" name="contact_name" placeholder="John" />
                    <TextInput icon={ICON_PERSON} label="Last Name" name="lastName" placeholder="Boston" />
                  </div>

                  <TextInput
                    icon={ICON_ENVELOPE}
                    label="Email"
                    name="email"
                    type="email"
                    placeholder="you@business.com"
                    validate={composeSyncValidators(required, validEmail())}
                    fullWidth
                  />

                  <div className="auth-form-grid">
                    <Field name="password" validate={required}>
                      {({ input, meta }) => (
                        <label className="auth-field auth-field-plain">
                          <img src={ICON_KEY} alt="" className="auth-field-icon" />
                          <div>
                            <div className="auth-field-label">Password</div>
                            <input
                              {...input}
                              type={showPassword ? "text" : "password"}
                              placeholder="••••••••••••"
                              autoComplete="new-password"
                            />
                          </div>
                          <button
                            type="button"
                            className="auth-field-toggle"
                            onClick={() => setShowPassword(s => !s)}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                          </button>
                          {meta.touched && meta.error && (
                            <span className="auth-field-error">{meta.error}</span>
                          )}
                        </label>
                      )}
                    </Field>

                    <Field name="confirmPassword" validate={required}>
                      {({ input, meta }) => (
                        <label className="auth-field auth-field-plain">
                          <img src={ICON_KEY} alt="" className="auth-field-icon" />
                          <div>
                            <div className="auth-field-label">Confirm Password</div>
                            <input
                              {...input}
                              type={showConfirmPassword ? "text" : "password"}
                              placeholder="••••••••••••"
                              autoComplete="new-password"
                            />
                          </div>
                          <button
                            type="button"
                            className="auth-field-toggle"
                            onClick={() => setShowConfirmPassword(s => !s)}
                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                          >
                            {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                          </button>
                          {meta.touched && meta.error && (
                            <span className="auth-field-error">{meta.error}</span>
                          )}
                        </label>
                      )}
                    </Field>
                  </div>
                </div>

                {/* ── Business Information ── */}
                <div className="auth-form-section">
                  <div className="auth-section-heading">Business Information</div>

                  <TextInputSvgIcon icon={BriefcaseIcon} label="Business Name" name="name" placeholder="Boston Pizza" />

                  <Field name="phone" validate={composeSyncValidators(required, validPhone())} parse={phoneFieldMask}>
                    {({ input, meta }) => (
                      <label className="auth-field auth-field-plain">
                        <span className="auth-phone-flag">🇨🇦 <ChevronDownIcon /></span>
                        <div>
                          <div className="auth-field-label">Phone</div>
                          <input {...input} type="tel" placeholder="+1 - 000-000-0000" maxLength={14} />
                        </div>
                        {meta.touched && meta.error && (
                          <span className="auth-field-error">{meta.error}</span>
                        )}
                      </label>
                    )}
                  </Field>

                  <Field name="street">
                    {({ input, meta }) => (
                      <label className="auth-field auth-field-plain auth-field-full">
                        <PinIcon />
                        <div>
                          <div className="auth-field-label">Business Address</div>
                          <input
                            {...input}
                            type="text"
                            placeholder="Street, City, Province, Postal Code, Country"
                            autoComplete="off"
                            onChange={e => {
                              input.onChange(e);
                              debouncedFetchAddressSuggestions(e.target.value);
                            }}
                          />
                        </div>
                        {meta.touched && meta.error && (
                          <span className="auth-field-error">{meta.error}</span>
                        )}
                        {addressSuggestions.length > 0 && (
                          <div className="auth-address-suggestions">
                            {addressSuggestions.map((s, index) => (
                              <button
                                key={s.place_id || index}
                                type="button"
                                className="auth-address-suggestion"
                                onClick={async () => {
                                  const typed = input.value;
                                  input.onChange(s.description);
                                  await fetchPlaceDetails(form, s.place_id, [typed, s.description]);
                                }}
                              >
                                {s.description}
                              </button>
                            ))}
                          </div>
                        )}
                      </label>
                    )}
                  </Field>

                  
                  <button
                    type="button"
                    className="auth-link"
                    style={{
                      background: "none", border: "none", padding: 0,
                      margin: "-8px 0 20px", cursor: "pointer",
                      font: "inherit", fontSize: 13, textAlign: "left",
                    }}
                    onClick={() =>
                      setAddressHelp({
                        name: [values.contact_name, values.lastName].filter(Boolean).join(" "),
                        email: values.email || "",
                        business: values.name || "",
                        address: values.street || "",
                      })
                    }
                  >
                    Can&apos;t find your address? Contact us
                  </button>

                  <div className="auth-form-grid">
                    <TextInput
                      icon={ICON_BUILDING}
                      label="City"
                      name="city"
                      placeholder="Pick an address above"
                      format={city => city?.name || ""}
                      disabled
                    />
                    <TextInput
                      icon={ICON_FLAG}
                      label="Province"
                      name="province"
                      placeholder="Pick an address above"
                      disabled
                    />
                  </div>
                  {cityError && <div className="auth-error">{cityError}</div>}

                  <div className="auth-form-grid">
                    <TextInputSvgIcon
                      icon={ScanIcon}
                      label="Postal Code"
                      name="postalCode"
                      placeholder="Pick an address above"
                      disabled
                    />
                    <TextInputSvgIcon
                      icon={GlobeIcon}
                      label="Country"
                      name="country"
                      placeholder="Pick an address above"
                      disabled
                    />
                  </div>

                  {/* ── Service area toggle — always visible, not tied to street ── */}
                  <div className="auth-form-section">
                    <div className="mp-radio-list">
                      <Field name="is_service_area_business" type="checkbox">
                        {({ input }) => (
                          <label
                            className={`mp-radio-row${input.checked ? " is-checked" : ""}`}
                            style={{
                              border: "none",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-start",
                              gap: "10px",
                            }}
                          >
                            <input
                              {...input}
                              className="mp-radio-input"
                              onChange={e => {
                                const checked = e.target.checked;
                                input.onChange(checked);
                                if (!checked) {
                                  form.change("additional_cities", []);
                                  form.change("additionalProvinces", undefined);
                                }
                              }}
                            />
                            <span className="mp-radio-dot" style={{ margin: 0 }} />
                            <span>I deliver goods or provide services to customers at their location.</span>
                          </label>
                        )}
                      </Field>
                    </div>
                  </div>

                  {values.is_service_area_business && (
                    <>
                      <ServiceableAreaSection
                        provinceOptions={provinceOptions}
                        cityOptions={cityOptions}
                        values={values}
                        form={form}
                      />
                      <Field name="additional_cities" subscription={{ touched: true, error: true }}>
                        {({ meta }) =>
                          meta.touched && meta.error ? (
                            <span className="auth-field-error">{meta.error}</span>
                          ) : null
                        }
                      </Field>
                    </>
                  )}

                  {/* ── Terms and Conditions — required to submit ── */}
                  <div className="auth-form-section">
                    <div className="mp-radio-list">
                      <Field name="agreed_to_terms" type="checkbox">
                        {({ input, meta }) => (
                          <>
                            <label
                              className={`mp-radio-row${input.checked ? " is-checked" : ""}`}
                              style={{
                                border: "none",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                gap: "10px",
                              }}
                            >
                              <input {...input} className="mp-radio-input" />
                              <span className="mp-radio-dot" style={{ margin: 0 }} />
                              <span>
                                I agree to the{" "}
                                <a
                                  href="https://www.mycouponbook.ca/terms-of-service/business"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Terms and Conditions
                                </a>
                              </span>
                            </label>
                            {(meta.touched || meta.submitFailed) && meta.error && (
                              <span className="auth-field-error">{meta.error}</span>
                            )}
                          </>
                        )}
                      </Field>
                    </div>
                  </div>
                </div>

                {errorMessage && <div className="auth-error">{errorMessage}</div>}

                <div className="auth-form-actions">
                  {onBack && (
                    <button type="button" className="auth-btn auth-btn-outline" onClick={onBack} disabled={loading}>
                      Back
                    </button>
                  )}
                  <button type="submit" className="auth-btn auth-btn-solid" disabled={loading || submitting || resolvingCity}>
                    {loading ? "Creating account..." : "Create Account"}
                  </button>
                </div>
              </form>
            )}
          />
        </div>
      </div>
      
      {addressHelp && (
        <AddressHelpModal
          initial={addressHelp}
          dispatch={dispatch}
          onClose={() => setAddressHelp(null)}
        />
      )}
    </div>
  );
};

BusinessDetailsPage.propTypes = {
  prefill: PropTypes.object,
  initialValues: PropTypes.object,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  code: PropTypes.string,
  setSignUpFlowActive: PropTypes.func,
  dispatch: PropTypes.func.isRequired,
  locations: PropTypes.object,
};

const mapStateToProps = state => ({
  locations: state.locations,
});

export default connect(mapStateToProps)(BusinessDetailsPage);